import React, { useMemo, useState } from 'react';
import { Box, IconButton } from '@mui/material';
// TODO: Replace with svg
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';

import { DataTable, type TableColumn } from '@/src/ui';
import type { StudentStandardsSummaryDTO } from '../models/studentStandardsReportDTOs';
import { ReportHeaderStyle, ReportCellStyle } from '@/src/utils';

export interface OpenStudentMasteryPayload {
    studentId: number;
    studentName: string;
    standards: Array<{ standardId: number; standardCode: string }>;
}

interface StudentStandardsReportTableProps {
    data: StudentStandardsSummaryDTO[];
    emptyMessage?: string;
    onOpenMastery: (payload: OpenStudentMasteryPayload) => void;
    pdfFileName?: string | (() => string);
}

const StudentStandardsReportTable: React.FC<StudentStandardsReportTableProps> = ({
    data,
    emptyMessage,
    onOpenMastery,
    pdfFileName,
}) => {
    const [openMap, setOpenMap] = useState<Record<number, boolean>>({});
    const toggle = (id: number) => setOpenMap((prev) => ({ ...prev, [id]: !prev[id] }));

    const rows = useMemo(() => {
        const out: any[] = [];
        for (const s of data ?? []) {
            const standardOptions = (s.standards ?? []).map((st) => ({
                standardId: st.standardId,
                standardCode: st.standardCode,
            }));

            const totalCorrect = (s.standards ?? []).reduce(
                (sum, st) => sum + (st.questionsCorrect ?? 0),
                0
            );
            const totalTotal = (s.standards ?? []).reduce(
                (sum, st) => sum + (st.questionsTotal ?? 0),
                0
            );
            const studentPercent =
                totalTotal > 0 ? Math.round((totalCorrect / totalTotal) * 100) : 0;
            const classCount = (s.standards ?? []).length;
            const classPercent =
                classCount > 0
                    ? Math.round(
                          (s.standards ?? []).reduce(
                              (sum, st) => sum + (st.classPercentCorrect ?? 0),
                              0
                          ) / classCount
                      )
                    : 0;

            out.push({
                isHeader: true,
                studentId: s.studentId,
                studentName: s.studentName,
                standardOptions,
                displayCorrect: totalCorrect,
                displayTotal: totalTotal,
                displayPercent: studentPercent,
                classPercentCorrect: classPercent,
            });

            if (openMap[s.studentId]) {
                // Already grouped by backend: each item is a parent row with optional subs inline
                const sorted = [...(s.standards ?? [])].sort((a, b) =>
                    (a.standardCode || '').localeCompare(b.standardCode || '', undefined, {
                        sensitivity: 'base',
                        numeric: true,
                    })
                );

                for (const st of sorted) {
                    out.push({
                        isHeader: false,
                        studentId: s.studentId,
                        studentName: s.studentName,
                        standardId: st.standardId,
                        standardCode: st.standardCode,
                        subs: Array.isArray(st.substandardCodes) ? st.substandardCodes : [],
                        skillDescription: st.skillDescription,
                        displayCorrect: st.questionsCorrect,
                        displayTotal: st.questionsTotal,
                        displayPercent: st.percentCorrect,
                        classPercentCorrect: st.classPercentCorrect,
                    });
                }
            }
        }
        return out;
    }, [data, openMap]);

    const columns: TableColumn[] = [
        {
            dataKey: 'studentName',
            header: 'Student',
            allowSort: false,
            align: 'left',
            emptyValue: '',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
            formatter: (row: any) => {
                if (row.isHeader) {
                    const standardOptions = row.standardOptions ?? [];

                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton
                                size="small"
                                aria-label={openMap[row.studentId] ? 'Collapse' : 'Expand'}
                                onClick={() => toggle(row.studentId)}
                                sx={{
                                    p: 0.25,
                                    color: 'inherit',
                                    '&:hover': { backgroundColor: 'transparent', color: 'black' },
                                }}
                            >
                                {openMap[row.studentId] ? (
                                    <KeyboardArrowUp />
                                ) : (
                                    <KeyboardArrowDown />
                                )}
                            </IconButton>

                            <a
                                href="#"
                                title="View student mastery"
                                style={{ fontWeight: 600 }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    onOpenMastery({
                                        studentId: row.studentId,
                                        studentName: row.studentName,
                                        standards: standardOptions,
                                    });
                                }}
                            >
                                {row.studentName || 'Unknown'}
                            </a>
                        </Box>
                    );
                }
                return '';
            },
        },
        {
            dataKey: 'standardCode',
            header: 'Standard',
            allowSort: false,
            align: 'left',
            emptyValue: '',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
            formatter: (row: any) =>
                row.isHeader ? (
                    ''
                ) : (
                    <span
                        style={{ whiteSpace: 'nowrap', display: 'inline-block', lineHeight: 1.4 }}
                    >
                        {row.standardCode || ''}
                        {Array.isArray(row.subs) && row.subs.length > 0 && (
                            <span style={{ opacity: 0.9 }}>
                                {', '}
                                {row.subs.map((c: string, i: number) => (
                                    <React.Fragment key={i}>
                                        {c}
                                        {i < row.subs.length - 1 ? ', ' : ''}
                                    </React.Fragment>
                                ))}
                            </span>
                        )}
                    </span>
                ),
        },
        {
            dataKey: 'skillDescription',
            header: 'Skill / Description',
            allowSort: false,
            align: 'left',
            emptyValue: '',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
            formatter: (row: any) => (row.isHeader ? '' : row.skillDescription || ''),
        },
        {
            dataKey: 'questionsCorrect',
            header: 'Correct',
            allowSort: false,
            align: 'center',
            emptyValue: '',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
            formatter: (row: any) => {
                const text = `${row.displayCorrect ?? 0}/${row.displayTotal ?? 0}`;
                return row.isHeader ? <strong>{text}</strong> : text;
            },
        },
        {
            dataKey: 'percentCorrect',
            header: 'Student %',
            allowSort: false,
            align: 'center',
            emptyValue: '',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
            formatter: (row: any) => {
                const text = `${row.displayPercent ?? 0}%`;
                return row.isHeader ? <strong>{text}</strong> : text;
            },
        },
        {
            dataKey: 'classPercentCorrect',
            header: 'Class %',
            allowSort: false,
            align: 'center',
            emptyValue: '',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
            formatter: (row: any) => {
                const text = `${row.classPercentCorrect ?? 0}%`;
                return row.isHeader ? <strong>{text}</strong> : text;
            },
        },
    ];

    return (
        <div className="w-full overflow-auto">
            <br />

            {data && data.length > 0 && (
                <div className="pl-4">
                    <h2
                        className="inline-block text-lg px-2 py-2 pl-4 bg-[var(--pm-secondary-light)] text-[var(--pm-dark)] rounded-t-lg tracking-wide whitespace-nowrap"
                        style={{
                            fontFamily: 'Verdana, Geneva, sans-serif',
                            boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.4)',
                        }}
                    >
                        Student Standards Report
                    </h2>
                </div>
            )}

            <DataTable
                data={rows}
                columns={columns}
                tableName="StudentStandardsReportTable"
                emptyMessage={emptyMessage}
                rowsPerPageInitial={50}
                enableExportCSV
                enableExportExcel
                enableExportPDF
                pdfFileName={pdfFileName}
                alternatingRowColor
                defaultSortColumn="studentName"
                defaultSortDirection="asc"
            />
        </div>
    );
};

export default StudentStandardsReportTable;
